import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ChatAudioPlayer.css";
import IconButton from "../base/IconButton";
import FlexRow from "../base/FlexRow";

type ChatAudioPlayerProps = {
    src: string;
    /** Called each time playback starts, e.g. to record who listened to a call. */
    onPlay?: () => void;
};

/** The speeds the toggle steps through, back to the start after the last. */
const RATES = [1, 1.5, 2];
const RATE_KEY = "chat-audio-rate";
const RATE_EVENT = "chat-audio-rate";

const readRate = () => {
    try {
        const saved = Number(localStorage.getItem(RATE_KEY));
        return RATES.includes(saved) ? saved : 1;
    } catch {
        return 1;
    }
};

const formatTime = (value: number) => {
    if (!value || !Number.isFinite(value)) return "0:00";

    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60);

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/* A bar and the gap after it, in pixels. As many as fit the width are drawn, close together. */
const BAR_STEP = 5;

/* The same bars for the same note, every time: the address, spun into heights. It is the very
   arithmetic the apps use (Java's String.hashCode and the same 64-bit generator), so a note starts
   with the same shape in the console as on the phone. Not the real sound -- the server keeps no
   waveform -- but a note looks like itself rather than reshuffling on every render. */
const barHeights = (key: string, count: number) => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
    }

    const mask = (1n << 64n) - 1n;
    let seed = BigInt.asUintN(64, BigInt(hash)) | 1n;

    return Array.from({ length: count }, () => {
        seed = (seed * 6364136223846793005n + 1442695040888963407n) & mask;
        return 0.25 + Number((seed >> 33n) & 0xFFn) / 255 * 0.75;
    });
};

export const ChatAudioPlayer: React.FC<ChatAudioPlayerProps> = ({ src, onPlay }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const barsRef = useRef<HTMLDivElement | null>(null);

    const animationRef = useRef<number | null>(null);

    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [rate, setRate] = useState<number>(readRate);

    /* How many bars fit, measured off the bubble as it is drawn. */
    const [barCount, setBarCount] = useState(0);

    useEffect(() => {
        const box = barsRef.current;
        if (!box) return;

        const measure = () => setBarCount(Math.max(10, Math.floor((box.clientWidth + 2) / BAR_STEP)));
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(box);

        return () => observer.disconnect();
    }, []);

    const bars = useMemo(() => barHeights(src, barCount), [src, barCount]);

    const progress = duration > 0 ? currentTime / duration : 0;
    /* Playing, or paused part-way: the note in hand. Only it carries the speed toggle. */
    const started = isPlaying || currentTime > 0;

    const togglePlay = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            audio.playbackRate = rate;
            await audio.play();
        } else {
            audio.pause();
        }
    };

    /* A click on the bars goes to that point, and starts the note there if it wasn't playing. */
    const handleSeek = async (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        const box = barsRef.current?.getBoundingClientRect();
        if (!audio || !box || !duration) return;

        const fraction = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
        const nextTime = fraction * duration;

        audio.currentTime = nextTime;
        setCurrentTime(nextTime);

        if (audio.paused) {
            audio.playbackRate = rate;
            await audio.play();
        }
    };

    /* One speed for every note: the choice is kept, and every player on the page follows it. */
    const nextRate = () => {
        const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length];

        try { localStorage.setItem(RATE_KEY, String(next)); } catch { }

        window.dispatchEvent(new CustomEvent(RATE_EVENT, { detail: { rate: next } }));
    };

    const updateProgress = () => {
        const audio = audioRef.current;
        if (!audio) return;

        setCurrentTime(audio.currentTime);

        animationRef.current = requestAnimationFrame(updateProgress);
    };

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = rate;
    }, [rate]);

    useEffect(() => {

        const handleOtherAudioPlay = (event: Event) => {
            const customEvent = event as CustomEvent;

            // Ignore self
            if (customEvent.detail?.src === src) {
                return;
            }

            const audio = audioRef.current;

            if (!audio) return;

            /* Another note took over: this one is stopped, not paused, so it goes back to the
               start and lets go of the speed toggle. */
            audio.pause();
            audio.currentTime = 0;
            setCurrentTime(0);
        };

        const handleRate = (event: Event) => {
            const next = (event as CustomEvent).detail?.rate;
            if (RATES.includes(next)) setRate(next);
        };

        window.addEventListener(
            "chat-audio-play",
            handleOtherAudioPlay
        );
        window.addEventListener(RATE_EVENT, handleRate);


        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }

            window.removeEventListener(
                "chat-audio-play",
                handleOtherAudioPlay
            );
            window.removeEventListener(RATE_EVENT, handleRate);
        };
    }, []);

    return (
        <FlexRow
            className="w-100 chat-audio" alignItems="center"
            style={{
                overflow: "hidden",
            }}
        >
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onLoadedMetadata={(e) => {
                    const audio = e.currentTarget;

                    /* A note recorded in a browser (webm) says it lasts forever until it has
                       been read to the end. Asking for a point far past the end makes the
                       browser work out the real length, which then arrives as a change. */
                    if (audio.duration === Infinity) {
                        audio.currentTime = 1e101;
                        return;
                    }

                    setDuration(audio.duration || 0);
                }}
                onDurationChange={(e) => {
                    const audio = e.currentTarget;

                    if (Number.isFinite(audio.duration) && audio.duration > 0 && duration === 0) {
                        setDuration(audio.duration);
                        if (audio.paused) audio.currentTime = 0;
                    }
                }}
                onPlay={() => {
                    setIsPlaying(true);
                    if (onPlay) onPlay();

                    window.dispatchEvent(
                        new CustomEvent("chat-audio-play", {
                            detail: {
                                src,
                            },
                        })
                    );

                    if (animationRef.current) {
                        cancelAnimationFrame(animationRef.current);
                    }

                    animationRef.current = requestAnimationFrame(updateProgress);
                }}
                onPause={() => {
                    setIsPlaying(false);

                    if (animationRef.current) {
                        cancelAnimationFrame(animationRef.current);
                    }
                }}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);

                    if (animationRef.current) {
                        cancelAnimationFrame(animationRef.current);
                    }
                }}
            />

            <div
                className="me-4 flex-shrink-0"
                style={{
                    width: 36,
                }}
            >
                <IconButton
                    icon={isPlaying ? "pause" : "play"}
                    type="solid"
                    size={18}
                    gap={24}
                    className="bg-primary text-light"
                    onClick={togglePlay}
                />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div ref={barsRef} className="chat-audio-bars" onClick={handleSeek}>
                    {
                        bars.map((height, index) =>
                            <span key={index}
                                className={started && index / bars.length <= progress ? "played" : ""}
                                style={{ height: `${Math.round(height * 100)}%` }} />
                        )
                    }
                </div>

                <FlexRow alignItems="center" className="mt-1 chat-audio-foot">
                    <div className="fs-8 text-muted chat-audio-clock">
                        {formatTime(started ? currentTime : duration)}
                    </div>

                    {
                        started &&
                        <button type="button" className="chat-audio-rate ms-auto" title="Playback speed"
                            onClick={nextRate}>
                            {rate}x
                        </button>
                    }
                </FlexRow>
            </div>
        </FlexRow>
    );
};
